import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, TextBox, theme } from "uikit";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";
import { uniqBy } from "lodash";

import Payment from "../../services/Payment";
import { MessageTemplateActions } from "../../types/MessageTemplateEnums";
import EditModal from "../EditModal";
import LabeledField from "../LabeledField";
import { useNotification } from "../NotificationProvider";

const T = "paymentLinkSms";

/** Phone for search and sending: international format, 11-15 digits. */
const MIN_PHONE_DIGITS = 11;
const MAX_PHONE_DIGITS = 15;

const digitsOf = (value: string) => value.replace(/\D/g, "");

const HINT_COLORS = {
	info: theme.colors.accent,
	error: "#d04141",
	muted: theme.colors.secondary,
} as const;

const Header = styled(Column)`
	padding-left: 20px;
	height: 50px;
	background: ${theme.colors.button_secondary};
	justify-content: center;

	font-size: 20px;
	line-height: 1;
	font-weight: 700;
	color: ${theme.colors.primary};
`;

const Root = styled(Column)`
	padding: 25px;
	/* Fixed: long hints wrap instead of widening the modal. */
	width: 520px;
	box-sizing: border-box;
`;

const Hint = styled.div<{ tone: keyof typeof HINT_COLORS }>`
	margin-top: 6px;
	white-space: normal;
	overflow-wrap: anywhere;
	font-size: 13px;
	line-height: 1.35;
	color: ${({ tone }) => HINT_COLORS[tone]};
`;

const HintLink = styled.span`
	cursor: pointer;
	text-decoration: underline;
`;

const CandidateTitle = styled.div`
	margin-top: 4px;
	font-weight: 600;

	&:first-child {
		margin-top: 0;
	}
`;

const CandidateRow = styled.div`
	padding-left: 8px;
`;

const Warning = styled.div`
	padding: 10px 12px;
	border-radius: 4px;
	background: rgba(255, 159, 0, 0.12);
	color: ${theme.colors.primary};
	font-size: 13px;
	line-height: 1.35;
`;

const formatAmount = (candidate: Payment.PaymentLinkCandidate) => {
	if (candidate.amount == null) return "";
	const symbol = candidate.currency?.symbol ?? candidate.currency?.code ?? "";
	return `${candidate.amount} ${symbol}`.trim();
};

/**
 * Sends the customer a link to pay a cash order by card via SMS. Any phone
 * number works: the passenger is not always the person who ordered. The
 * phone suggests an open order; the order number can also be typed in.
 */
const PaymentLinkSmsModal: React.FC<PaymentLinkSmsModal.Props> = ({
	initialPhone,
	initialOrderNumber,
	smsTemplateId,
	onClose,
	onSent,
}) => {
	const { t } = useTranslation();
	const notification = useNotification();

	const [phone, setPhone] = useState(initialPhone ?? "");
	const [orderNumberText, setOrderNumberText] = useState(
		initialOrderNumber ? String(initialOrderNumber) : "",
	);
	/** The order number was typed by hand: the phone search must not override it. */
	const [orderTouched, setOrderTouched] = useState(
		Boolean(initialOrderNumber),
	);

	const [phoneCandidates, setPhoneCandidates] = useState<
		Payment.PaymentLinkCandidate[] | null
	>(null);
	const [phoneLoading, setPhoneLoading] = useState(false);

	const [order, setOrder] = useState<Payment.PaymentLinkCandidate | null>(
		null,
	);
	/**
	 * Orders found by number during this modal (e.g. the opened order): kept
	 * in the list so the dispatcher can switch back to them by a click even
	 * when the phone search (latest orders only) does not return them.
	 */
	const [lookedUpOrders, setLookedUpOrders] = useState<
		Payment.PaymentLinkCandidate[]
	>([]);
	const [orderLookup, setOrderLookup] = useState<
		"idle" | "loading" | "found" | "notFound"
	>("idle");

	const [sending, setSending] = useState(false);
	const [errorText, setErrorText] = useState<string | null>(null);
	/** The driver app did not confirm the taximeter pause: next send uses force. */
	const [taximeterNotConfirmed, setTaximeterNotConfirmed] = useState(false);

	const reasonText = useCallback(
		(reason: string | null | undefined): string => {
			switch (reason) {
				case "not_executing":
					return t(`${T}.reason.notExecuting`) ?? "";
				case "no_executor":
					return t(`${T}.reason.noExecutor`) ?? "";
				case "not_cash":
					return t(`${T}.reason.notCash`) ?? "";
				case "already_paid":
					return t(`${T}.reason.alreadyPaid`) ?? "";
				case "sms_link_disabled":
					return t(`${T}.reason.smsLinkDisabled`) ?? "";
				case "sms_template_missing":
					return t(`${T}.reason.templateMissing`) ?? "";
				case "sms_provider_missing":
					return t(`${T}.reason.providerMissing`) ?? "";
				case "invalid_phone":
					return t(`${T}.reason.invalidPhone`) ?? "";
				default:
					return t(`${T}.reason.unknown`) ?? "";
			}
		},
		[t],
	);

	/** Amount and driver of the order, without its number. */
	const describeDetails = useCallback(
		(candidate: Payment.PaymentLinkCandidate) =>
			[
				formatAmount(candidate),
				candidate.executor
					? `${t(`${T}.executor`) ?? ""} ${
							candidate.executor.callSign ??
							candidate.executor.name ??
							""
					  }`.trim()
					: "",
			]
				.filter(Boolean)
				.join(" · "),
		[t],
	);

	const describeOrder = useCallback(
		(candidate: Payment.PaymentLinkCandidate) =>
			[
				`${t(`${T}.order`) ?? ""} №${candidate.orderNumber}`,
				describeDetails(candidate),
			]
				.filter(Boolean)
				.join(" · "),
		[describeDetails, t],
	);

	// Open orders for the phone: a hint under the field and, while the order
	// number is untouched, the first suitable order filled in.
	const searchByPhone = useDebouncedCallback(async (value: string) => {
		const digits = digitsOf(value);
		if (digits.length < MIN_PHONE_DIGITS) {
			setPhoneCandidates(null);
			setPhoneLoading(false);
			return;
		}
		setPhoneLoading(true);
		try {
			const items = await Payment.findOrdersForPaymentLink({
				phone: digits,
			});
			setPhoneCandidates(items);
			const suggested = items.find((item) => item.available) ?? items[0];
			if (suggested && !orderTouched) {
				setOrderNumberText(String(suggested.orderNumber));
			}
		} catch {
			setPhoneCandidates([]);
		} finally {
			setPhoneLoading(false);
		}
	}, 400);

	const lookupOrder = useDebouncedCallback(async (value: string) => {
		const orderNumber = Number(digitsOf(value));
		if (!orderNumber) {
			setOrder(null);
			setOrderLookup("idle");
			return;
		}
		setOrderLookup("loading");
		try {
			const [found] = await Payment.findOrdersForPaymentLink({
				orderNumber,
			});
			setOrder(found ?? null);
			setOrderLookup(found ? "found" : "notFound");
			if (found) {
				setLookedUpOrders((prev) =>
					prev.some((item) => item.id === found.id)
						? prev
						: [...prev, found],
				);
			}
		} catch {
			setOrder(null);
			setOrderLookup("notFound");
		}
	}, 400);

	useEffect(() => {
		searchByPhone(phone);
	}, [phone, searchByPhone]);

	useEffect(() => {
		setTaximeterNotConfirmed(false);
		setErrorText(null);
		lookupOrder(orderNumberText);
	}, [orderNumberText, lookupOrder]);

	const handlePhoneChange = useCallback((value: string) => {
		setErrorText(null);
		setPhone(value);
	}, []);

	const handleOrderNumberChange = useCallback((value: string) => {
		setOrderTouched(true);
		setOrderNumberText(digitsOf(value));
	}, []);

	const phoneDigits = digitsOf(phone);
	const phoneValid =
		phoneDigits.length >= MIN_PHONE_DIGITS &&
		phoneDigits.length <= MAX_PHONE_DIGITS;
	// A pending link can be resent to another number.
	const orderSendable = Boolean(
		order && (order.available || order.qrPayment?.status === "pending"),
	);

	const handleSubmit = useCallback(async () => {
		if (!order || !phoneValid || sending) return;
		setSending(true);
		setErrorText(null);
		try {
			const res = await Payment.sendOrderPaymentLinkSms({
				orderId: order.id,
				phone: phoneDigits,
				...(smsTemplateId ? { smsTemplateId } : {}),
				...(taximeterNotConfirmed ? { force: true } : {}),
			});
			notification.success(
				t(`${T}.sent`) ?? "",
				`№${order.orderNumber} · ${res?.amount ?? ""} → +${
					res?.phone ?? phoneDigits
				}`,
			);
			onSent?.();
			onClose();
		} catch (error) {
			const message = String((error as Error)?.message ?? "");
			if (
				message.startsWith("ORDER_PAYMENT_LINK_TAXIMETER_NOT_CONFIRMED")
			) {
				setTaximeterNotConfirmed(true);
			} else if (message.startsWith("ORDER_PAYMENT_LINK_NOT_AVAILABLE")) {
				setErrorText(reasonText(message.split(":")[1]));
			} else if (message.startsWith("ORDER_PAYMENT_LINK_CANCEL_FAILED")) {
				setErrorText(t(`${T}.cancelFailed`) ?? "");
			} else {
				setErrorText(message || (t(`${T}.sendFailed`) ?? ""));
			}
		} finally {
			setSending(false);
		}
	}, [
		notification,
		onClose,
		onSent,
		order,
		phoneDigits,
		phoneValid,
		reasonText,
		sending,
		smsTemplateId,
		t,
		taximeterNotConfirmed,
	]);

	// Open orders for the phone: the current one is the order in the order
	// number field (or the suggested one), the rest are listed below it. Any
	// number can be clicked to pick that order.
	const sendableCandidates = useMemo(
		() =>
			uniqBy(
				[...lookedUpOrders, ...(phoneCandidates ?? [])],
				"id",
			).filter(
				(item) =>
					item.available || item.qrPayment?.status === "pending",
			),
		[lookedUpOrders, phoneCandidates],
	);

	// The order in the number field is the current one even when the phone
	// search (latest orders only) did not return it.
	const currentCandidate = useMemo(() => {
		if (order && String(order.orderNumber) === orderNumberText) {
			return order;
		}
		return (
			(phoneCandidates ?? []).find(
				(item) => String(item.orderNumber) === orderNumberText,
			) ?? sendableCandidates[0]
		);
	}, [order, orderNumberText, phoneCandidates, sendableCandidates]);

	const otherCandidates = useMemo(
		() =>
			sendableCandidates.filter(
				(item) => item.id !== currentCandidate?.id,
			),
		[currentCandidate?.id, sendableCandidates],
	);

	const pickOrder = useCallback((item: Payment.PaymentLinkCandidate) => {
		setOrderTouched(true);
		setOrderNumberText(String(item.orderNumber));
	}, []);

	const renderCandidate = (item: Payment.PaymentLinkCandidate) => (
		<CandidateRow key={item.id}>
			<HintLink onClick={() => pickOrder(item)}>
				№{item.orderNumber}
			</HintLink>
			{describeDetails(item) && ` · ${describeDetails(item)}`}
		</CandidateRow>
	);

	const phoneHint = (() => {
		if (!phoneDigits) return null;
		if (!phoneValid) {
			return <Hint tone="muted">{reasonText("invalid_phone")}</Hint>;
		}
		if (phoneLoading) {
			return <Hint tone="muted">{t(`${T}.searching`)}</Hint>;
		}
		if (!phoneCandidates) return null;
		if (!phoneCandidates.length || !currentCandidate) {
			return <Hint tone="muted">{t(`${T}.noOrdersByPhone`)}</Hint>;
		}
		return (
			<Hint tone="info">
				<CandidateTitle>{t(`${T}.currentOrder`)}:</CandidateTitle>
				{renderCandidate(currentCandidate)}
				{otherCandidates.length > 0 && (
					<>
						<CandidateTitle>
							{t(`${T}.otherOrders`)}:
						</CandidateTitle>
						{otherCandidates.map(renderCandidate)}
					</>
				)}
			</Hint>
		);
	})();

	const orderHint = (() => {
		if (orderLookup === "loading") {
			return <Hint tone="muted">{t(`${T}.checking`)}</Hint>;
		}
		if (orderLookup === "notFound") {
			return <Hint tone="error">{t(`${T}.orderNotFound`)}</Hint>;
		}
		if (orderLookup !== "found" || !order) return null;
		if (!orderSendable) {
			return (
				<Hint tone="error">{reasonText(order.unavailableReason)}</Hint>
			);
		}
		return (
			<Hint tone="info">
				{describeOrder(order)}
				{order.qrPayment?.status === "pending" &&
					`. ${t(`${T}.pendingLink`) ?? ""}`}
			</Hint>
		);
	})();

	return (
		<EditModal
			onCancel={onClose}
			onSave={handleSubmit}
			loading={sending}
			canSave={Boolean(orderSendable && phoneValid && !sending)}
			footerJustify="center"
			submitText={
				(taximeterNotConfirmed
					? t(`${T}.sendAnyway`)
					: t(`${T}.send`)) ?? ""
			}
		>
			<Header>{t(`${T}.title`)}</Header>
			{/* The phone is prefilled: focus it without selecting the number. */}
			<Root gaps="20px*" data-no-autoselect>
				<LabeledField label={t(`${T}.phone`) ?? ""}>
					<TextBox.TextBox
						value={phone}
						placeholder="380…"
						onChange={handlePhoneChange}
						disabled={sending}
					/>
					{phoneHint}
				</LabeledField>
				<LabeledField label={t(`${T}.orderNumber`) ?? ""}>
					<TextBox.TextBox
						value={orderNumberText}
						placeholder="№"
						onChange={handleOrderNumberChange}
						disabled={sending}
					/>
					{orderHint}
				</LabeledField>
				{taximeterNotConfirmed && (
					<Warning>{t(`${T}.taximeterNotConfirmed`)}</Warning>
				)}
				{errorText && <Hint tone="error">{errorText}</Hint>}
			</Root>
		</EditModal>
	);
};

/**
 * The order payment link template (event `ORDER_PAYMENT_LINK`). Plain SMS
 * sending cannot send it as is: the link has to be issued for a specific
 * order, so sending goes through this modal. The older invoice template
 * (`INVOICE_PAYMENT`) belongs to automatic invoice sending and is not used here.
 */
export const isPaymentLinkTemplate = (template: {
	action?: MessageTemplateActions;
}): boolean => template?.action === MessageTemplateActions.ORDER_PAYMENT_LINK;

declare namespace PaymentLinkSmsModal {
	interface Props {
		/** Phone to prefill: the order phone or the selected customer phone. */
		initialPhone?: string;
		/** Order number when opened from a selected order. */
		initialOrderNumber?: number;
		/** Template chosen in plain SMS sending; the default template otherwise. */
		smsTemplateId?: number;
		onClose: () => void;
		onSent?: () => void;
	}
}

export {
	isPaymentLinkSmsAwaiting,
	isPaymentLinkSmsPending,
} from "../../utils/orderPaymentStatus";

export default memo(PaymentLinkSmsModal);
